import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, HelpCircle, Lightbulb, AlertTriangle, Trash2, CheckCircle, Send, Reply, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders, isFullAdmin } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPO_CONFIG = {
  ajuda: { icon: HelpCircle, label: 'Ajuda', color: 'bg-blue-100 text-blue-600' },
  sugestao: { icon: Lightbulb, label: 'Sugestão', color: 'bg-amber-100 text-amber-600' },
  erro: { icon: AlertTriangle, label: 'Erro', color: 'bg-red-100 text-red-600' },
  outro: { icon: MessageCircle, label: 'Outro', color: 'bg-gray-100 text-gray-600' },
};

const ListaFeedback = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});

  useEffect(() => { fetchFeedback(); }, []);

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API}/feedback`, { headers: getAdminHeaders() });
      setItems(res.data.items);
      setTotal(res.data.total);
      setUnread(res.data.unread);
    } catch { toast.error('Erro ao carregar mensagens'); }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/feedback/${id}/read`, {}, { headers: getAdminHeaders() });
      fetchFeedback();
    } catch { toast.error('Erro'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/feedback/${id}`, { headers: getAdminHeaders() });
      toast.success('Mensagem removida');
      fetchFeedback();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleReply = async (feedbackId) => {
    if (!replyText.trim()) { toast.error('Escreva uma resposta'); return; }
    setSendingReply(true);
    try {
      await axios.post(`${API}/feedback/${feedbackId}/reply`, { mensagem: replyText }, { headers: getAdminHeaders() });
      toast.success('Resposta enviada!');
      setReplyText('');
      setReplyingTo(null);
      setExpandedReplies(prev => ({ ...prev, [feedbackId]: true }));
      fetchFeedback();
    } catch { toast.error('Erro ao enviar resposta'); }
    finally { setSendingReply(false); }
  };

  const toggleReplies = (id) => {
    setExpandedReplies(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  if (loading) {
    return (<div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p className="font-inter text-gray-500">Carregando...</p></div>);
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <MessageCircle className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Mensagens</h1>
            {unread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{unread}</span>
            )}
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">{total} mensagens recebidas</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">
        {items.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Nenhuma mensagem recebida</p>
          </Card>
        ) : (
          items.map((item) => {
            const cfg = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.outro;
            const Icon = cfg.icon;
            const replies = item.respostas || [];
            const hasReplies = replies.length > 0;
            const isExpanded = expandedReplies[item.id];
            const isReplying = replyingTo === item.id;

            return (
              <Card key={item.id} data-testid={`feedback-card-${item.id}`} className={`bg-white rounded-xl shadow-sm border transition-all overflow-hidden ${item.lido ? 'border-[#E1ECE5]' : 'border-[#1A4D2E]/30 bg-[#FAFDF9]'}`}>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>
                        {!item.lido && <span className="w-2 h-2 bg-[#1A4D2E] rounded-full" />}
                        <span className="text-xs text-gray-400 ml-auto">{formatDate(item.created_at)}</span>
                      </div>
                      <p className="font-inter text-sm text-[#1A4D2E] mb-1">{item.mensagem}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {item.nome && <span>De: {item.nome}</span>}
                        {item.contato && <span>Contato: {item.contato}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {!item.lido && (
                        <button data-testid={`read-${item.id}`} onClick={() => markRead(item.id)} className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 rounded-full p-1.5 transition-all" title="Marcar como lido">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`reply-btn-${item.id}`}
                        onClick={() => { setReplyingTo(isReplying ? null : item.id); setReplyText(''); }}
                        className={`hover:bg-blue-50 rounded-full p-1.5 transition-all ${isReplying ? 'text-blue-600 bg-blue-50' : 'text-blue-400'}`}
                        title="Responder"
                      >
                        <Reply className="w-4 h-4" />
                      </button>
                      {isFullAdmin() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`delete-fb-${item.id}`} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-1.5 transition-all" title="Excluir">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir mensagem</AlertDialogTitle>
                              <AlertDialogDescription>Tem certeza que deseja excluir esta mensagem?</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>

                {/* Respostas existentes */}
                {hasReplies && (
                  <div className="border-t border-gray-100">
                    <button
                      data-testid={`toggle-replies-${item.id}`}
                      onClick={() => toggleReplies(item.id)}
                      className="w-full px-4 py-2 flex items-center gap-2 text-xs font-semibold text-[#1A4D2E] hover:bg-[#F2F7F4] transition-all"
                    >
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {replies.length} resposta{replies.length > 1 ? 's' : ''}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-3 space-y-2">
                        {replies.map((r) => (
                          <div key={r.id} className="bg-[#F2F7F4] rounded-lg p-3 ml-6 border-l-3 border-[#1A4D2E]" data-testid={`reply-item-${r.id}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-bold text-[#1A4D2E]">{r.autor}</span>
                              <span className="text-xs text-gray-400">{formatDate(r.created_at)}</span>
                            </div>
                            <p className="font-inter text-sm text-gray-700">{r.mensagem}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Formulário de resposta */}
                {isReplying && (
                  <div className="border-t border-gray-100 p-4 bg-[#FAFDF9]">
                    <div className="flex gap-2">
                      <textarea
                        data-testid={`reply-input-${item.id}`}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Escreva sua resposta..."
                        className="flex-1 rounded-xl border border-gray-200 bg-white p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] resize-none"
                        autoFocus
                      />
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setReplyingTo(null); setReplyText(''); }}
                        className="rounded-full text-xs"
                        data-testid={`cancel-reply-${item.id}`}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReply(item.id)}
                        disabled={sendingReply || !replyText.trim()}
                        className="rounded-full text-xs bg-[#1A4D2E] hover:bg-[#145A32] text-white gap-1"
                        data-testid={`send-reply-${item.id}`}
                      >
                        <Send className="w-3.5 h-3.5" />
                        {sendingReply ? 'Enviando...' : 'Enviar Resposta'}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
      <Navigation active="dashboard" />
    </div>
  );
};

export default ListaFeedback;
